import { LogOut, Package, User, UserPlus } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { routes } from '@/app/router/route-paths';
import { useAuth } from '@/features/auth';
import { useLogout } from '@/features/auth/hooks/useLogout';
import { cn } from '@/shared/utils/cn';

/**
 * Auth-aware account control for the storefront navbar.
 * Guest → links straight to the login/register page.
 * Signed-in → dropdown with profile, orders and sign-out.
 */
export function NavAccount({ iconClass }) {
  const { user } = useAuth();
  const logout = useLogout();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function onKey(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Guest: single tap to the auth page (login screen links onward to register).
  if (!user) {
    return (
      <Link to={routes.login} aria-label="Sign in or create account" className={iconClass}>
        <User size={19} strokeWidth={1.6} />
      </Link>
    );
  }

  const firstName = user.name?.split(' ')[0] || 'Account';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={cn(iconClass, open && 'bg-gold-50 text-gold-700')}
      >
        <User size={19} strokeWidth={1.6} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden rounded-2xl border border-[color:var(--color-line)] bg-white shadow-[var(--shadow-lift)]"
        >
          <div className="border-b border-[color:var(--color-line)] px-4 py-3">
            <p className="font-serif text-base text-[color:var(--color-ink)]">Hi, {firstName}</p>
            <p className="truncate text-xs text-[color:var(--color-muted)]">{user.email}</p>
          </div>
          <div className="p-1.5">
            <MenuLink to={routes.profile} icon={User} label="My profile" onClick={() => setOpen(false)} />
            <MenuLink to={routes.orders} icon={Package} label="My orders" onClick={() => setOpen(false)} />
            <button
              type="button"
              role="menuitem"
              disabled={logout.isPending}
              onClick={() => {
                setOpen(false);
                logout.mutate();
              }}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm text-[color:var(--color-body)] transition hover:bg-gold-50 hover:text-gold-700 disabled:opacity-60"
            >
              <LogOut size={17} strokeWidth={1.6} />
              Sign out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function MenuLink({ to, icon: Icon, label, onClick }) {
  return (
    <Link
      to={to}
      role="menuitem"
      onClick={onClick}
      className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-[color:var(--color-body)] transition hover:bg-gold-50 hover:text-gold-700"
    >
      <Icon size={17} strokeWidth={1.6} />
      {label}
    </Link>
  );
}

NavAccount.MenuLink = MenuLink;

// Compact guest CTA for the mobile drawer (register-focused).
export function NavAccountMobile({ onNavigate }) {
  const { user } = useAuth();
  const logout = useLogout();

  if (!user) {
    return (
      <div className="mt-6 grid gap-2">
        <Link
          to={routes.login}
          onClick={onNavigate}
          className="inline-flex items-center justify-center gap-2 rounded-full border border-gold-500 py-3 text-sm font-semibold text-gold-700"
        >
          <User size={17} strokeWidth={1.7} />
          Sign in
        </Link>
        <Link
          to={routes.register}
          onClick={onNavigate}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-gold-500 py-3 text-sm font-semibold text-white"
        >
          <UserPlus size={17} strokeWidth={1.7} />
          Create account
        </Link>
      </div>
    );
  }

  return (
    <div className="mt-6 grid gap-1">
      <Link
        to={routes.profile}
        onClick={onNavigate}
        className="flex items-center gap-3 py-3 font-serif text-lg text-[color:var(--color-ink)]"
      >
        <User size={18} strokeWidth={1.6} />
        My profile
      </Link>
      <Link
        to={routes.orders}
        onClick={onNavigate}
        className="flex items-center gap-3 py-3 font-serif text-lg text-[color:var(--color-ink)]"
      >
        <Package size={18} strokeWidth={1.6} />
        My orders
      </Link>
      <button
        type="button"
        onClick={() => {
          onNavigate?.();
          logout.mutate();
        }}
        className="flex items-center gap-3 py-3 text-left font-serif text-lg text-[color:var(--color-ink)]"
      >
        <LogOut size={18} strokeWidth={1.6} />
        Sign out
      </button>
    </div>
  );
}
